import { Box, Button, Flex, Icon, chakra } from '@chakra-ui/react';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { DateTime } from 'luxon';
import React, { useRef } from 'react';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import { FiCalendar } from 'react-icons/fi';

interface SimpleDatePickerProps {
  minDate?: Date | null;
  maxDate?: Date | null;
  selectedDate?: Date | null;
  setSelectedDate:
    | React.Dispatch<React.SetStateAction<Date>>
    | ((value: Date) => void);
  dateFormat?: string;
  width?: string;
  showTimeSelect?: boolean;
  tz?: string;
  showIcon?: boolean;
  placeHolder?: string;
  disabled?: boolean;
}

interface SimpleDatePickerWithOnChangeProps
  extends Omit<SimpleDatePickerProps, 'setSelectedDate'> {
  onChange: (date: Date) => void;
}

const offsetDate = (date: Date, tz: string): Date => {
  return new Date(
    DateTime.fromFormat(
      formatInTimeZone(date, tz, 'yyyy-MM-dd HH:mm:ss'),
      'yyyy-MM-dd HH:mm:ss',
    ).toISO(),
  );
};

const offsetMaybeDate = (
  date: Date | undefined | null,
  tz: string,
): Date | undefined => (date ? offsetDate(date, tz) : undefined);

// use chakra factory to allow chakra to modify 3rd party component styles
const ChakraReactDatePicker = chakra(DatePicker, {
  baseStyle: { bg: 'transparent' },
});

export const SimpleDatePicker: React.FC<SimpleDatePickerProps> = ({
  minDate,
  maxDate,
  selectedDate,
  setSelectedDate,
  dateFormat = 'yyyy-MM-dd',
  width = 'sm',
  showTimeSelect,
  tz = 'America/New_York',
  showIcon = false,
  placeHolder,
  disabled,
}) => {
  // DatePicker only renders in browser local time, requiring adjustment
  const offsetSelectedDate = offsetMaybeDate(selectedDate, tz);
  const offsetMinDate = offsetMaybeDate(minDate, tz);
  const offsetMaxDate = offsetMaybeDate(maxDate, tz);

  return (
    <Flex
      w={width}
      borderWidth="1px"
      borderRadius="5px"
      padding="6px 16px"
      minHeight="40px"
    >
      {showIcon && (
        <Icon
          as={FiCalendar}
          marginRight="10px"
          marginTop="-5px"
          h="inherit"
          pointerEvents="none"
        />
      )}
      <ChakraReactDatePicker
        width="100%"
        selected={offsetSelectedDate}
        onChange={(date: Date) => setSelectedDate(zonedTimeToUtc(date, tz))}
        minDate={offsetMinDate}
        maxDate={offsetMaxDate}
        dateFormat={dateFormat}
        showTimeSelect={showTimeSelect}
        placeholderText={placeHolder}
        disabled={disabled}
      />
    </Flex>
  );
};

export const SimpleDatePickerWithOnChange: React.FC<SimpleDatePickerWithOnChangeProps> = ({
  minDate,
  maxDate,
  selectedDate,
  onChange,
  dateFormat = 'yyyy-MM-dd',
  width = 'sm',
  showTimeSelect,
  tz = 'America/New_York',
  showIcon = false,
  placeHolder,
  disabled,
}) => {
  // DatePicker only renders in browser local time, requiring adjustment
  const offsetSelectedDate = offsetMaybeDate(selectedDate, tz);
  const offsetMinDate = offsetMaybeDate(minDate, tz);
  const offsetMaxDate = offsetMaybeDate(maxDate, tz);

  return (
    <Flex
      w={width}
      borderWidth="1px"
      borderRadius="5px"
      padding="6px 16px"
      minHeight="40px"
    >
      {showIcon && (
        <Icon
          as={FiCalendar}
          marginRight="10px"
          marginTop="-5px"
          h="inherit"
          pointerEvents="none"
        />
      )}
      <ChakraReactDatePicker
        width="100%"
        selected={offsetSelectedDate}
        onChange={onChange}
        minDate={offsetMinDate}
        maxDate={offsetMaxDate}
        dateFormat={dateFormat}
        showTimeSelect={showTimeSelect}
        placeholderText={placeHolder}
        disabled={disabled}
      />
    </Flex>
  );
};

/** Date picker with visual style matching our Select component. */
export const SelectStyleDatePicker: React.FC<SimpleDatePickerProps> = ({
  minDate,
  maxDate,
  selectedDate,
  setSelectedDate,
  dateFormat = 'yyyy-MM-dd',
  width = 'sm',
  showTimeSelect,
  tz = 'America/New_York',
  showIcon = false,
  placeHolder,
  disabled,
}) => {
  // DatePicker only renders in browser local time, requiring adjustment
  const offsetSelectedDate = offsetMaybeDate(selectedDate, tz);
  const offsetMinDate = offsetMaybeDate(minDate, tz);
  const offsetMaxDate = offsetMaybeDate(maxDate, tz);

  const datePicker = useRef<InstanceType<typeof DatePicker>>();
  return (
    <Button
      width={width}
      variant="outline"
      borderWidth="1px"
      borderRadius="5px"
      padding="6px 16px"
      minHeight="40px"
      as={Flex}
      justifyContent="flex-start"
      disabled={disabled}
      onClick={() => datePicker.current?.setOpen(true)}
      fontWeight="normal"
      cursor="pointer"
    >
      <Box
        flex={1}
        // Style elements within react date picker.
        sx={{
          // Expand to fill container's width.
          '& .react-datepicker-wrapper': { width: '100%' },
          '& input': { width: '100%' },
          // Disable the text input outline when focused.
          '& input:focus': { outline: 'none' },
          // Don't grey out the placeholder text.
          '& input::placeholder': { opacity: 1, color: 'inherit' },
        }}
      >
        <ChakraReactDatePicker
          ref={datePicker}
          selected={offsetSelectedDate}
          onChange={(date: Date) => setSelectedDate(zonedTimeToUtc(date, tz))}
          minDate={offsetMinDate}
          maxDate={offsetMaxDate}
          dateFormat={dateFormat}
          showTimeSelect={showTimeSelect}
          placeholderText={placeHolder}
          disabled={disabled}
          cursor="inherit"
        />
      </Box>
      {showIcon && (
        <Box ml="16px">
          <FiCalendar />
        </Box>
      )}
    </Button>
  );
};
